'use client';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useQuery } from '@parkchirp/api-client';
import { FeeTypeEnum } from 'parkchirp-shared/enums';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { GET_SUBLOTS } from '../../queries';
import type { SetShowSnackbar } from '../../types/alerts';
import type { DownloadReport } from '../../types/reports';
import { formatDateParkerStatus } from '../../utils';
import { ReportActionButton } from './reportActionButton';

interface PaymentReportProps {
	facilityId: string;
	downloadReport: DownloadReport;
	setShowSnackbar: SetShowSnackbar;
	forTenant?: boolean;
	prefillAccountId?: string;
	prefillSubName?: string;
}

export const PaymentReportForm: FC<PaymentReportProps> = ({
	facilityId,
	downloadReport,
	setShowSnackbar,
	prefillAccountId,
	prefillSubName,
	forTenant = false,
}) => {
	const [reportType, setReportType] = useState<string>();
	const [rateType, setRateType] = useState<string[]>([]);
	const [dateOrder, setDateOrder] = useState<string>();
	const [transactionStartDate, setTransactionStartDate] = useState<string | null>(null);
	const [transactionEndDate, setTransactionEndDate] = useState<string | null>(null);
	const [applicationStartDate, setApplicationStartDate] = useState<string | null>(null);
	const [applicationEndDate, setApplicationEndDate] = useState<string | null>(null);
	const [accountId, setAccountId] = useState<string>();
	const [subName, setSubName] = useState<string>();
	const [checkedTypes, setCheckedTypes] = useState<Record<string, boolean>>({});
	const [selectAll, setSelectAll] = useState<boolean>(true);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
	const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);
	const [selectedSublotId, setSelectedSublotId] = useState<null | number>(null);
	const [selectedSublotName, setSelectedSublotName] = useState<null | string>(null);

	const { data: sublotsData } = useQuery(GET_SUBLOTS, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		variables: {
			facilityId,
			includePrimary: true,
		},
		skip: !facilityId,
		fetchPolicy: 'network-only',
	});

	const reportTypes = [
		{ label: 'Payment Summary Report', value: 'PaymentSummaryReport' },
		{ label: 'Monthly Parking Payment Report', value: 'PaymentDetailReport' },
		{ label: 'Payment Reconciliation Report', value: 'ReconciliationReport' },
		{ label: 'Cash Basis Payment Report', value: 'CashBasisPaymentReport' },
	];
	const isCashBasisPaymentReport = useMemo(() => reportType === 'CashBasisPaymentReport', [reportType]);
	const isReconciliationReport = useMemo(() => reportType === 'ReconciliationReport', [reportType]);

	const rateTypes = useMemo(() => {
		let rateTypeOptions = [
			'Monthly',
			'Validation',
			'Hotel Storage',
			'Office/Retail Rent',
			'Miscellaneous',
			'Applied Credit',
		];

		if (reportType === 'CashBasisPaymentReport') {
			rateTypeOptions = rateTypeOptions.filter((rType) => rType !== 'Applied Credit');
			rateTypeOptions.push(...['Applied Cash Credit', 'Unapplied Cash Credit']);
		}
		return rateTypeOptions;
	}, [reportType]);

	const dateOrderOptions = useMemo(
		() => [
			{ label: 'Transaction Date', value: 'transaction' },
			{ label: 'Application Date', value: 'application' },
		],
		[],
	);

	const enableRateType = useMemo(
		() => ['CashBasisPaymentReport', 'PaymentSummaryReport'].includes(reportType),
		[reportType],
	);

	useEffect(() => {
		if (selectAll) {
			const initialCheckedItems: Record<string, boolean> = {};
			rateTypes.forEach((fee) => {
				initialCheckedItems[fee] = true;
			});
			setCheckedTypes(initialCheckedItems);
		}
	}, [selectAll, rateTypes]);

	const handleSingleCheck = useCallback(
		(value: string) => {
			setCheckedTypes((prev) => {
				const updatedItems = {
					...prev,
					[value]: !prev[value],
				};

				const allChecked = rateTypes?.every((fee) => updatedItems[fee]) ?? false;
				setSelectAll(allChecked);

				return updatedItems;
			});
		},
		[rateTypes],
	);

	const handleSelectAllCheck = useCallback(() => {
		if (selectAll) {
			setCheckedTypes({});
		} else {
			const newCheckedItems: Record<string, boolean> = {};
			rateTypes?.forEach((fee) => {
				newCheckedItems[fee] = true;
			});
			setCheckedTypes(newCheckedItems);
		}
		setSelectAll(!selectAll);
	}, [rateTypes, selectAll]);

	useEffect(() => {
		const checkedRateTypes = Object.keys(checkedTypes).filter((fee) => checkedTypes[fee]);
		setRateType(checkedRateTypes);
	}, [checkedTypes]);

	const formatRateType = useCallback((str: string) => {
		if (str === FeeTypeEnum.MONTHLY) {
			return ['MONTHLY', 'MANUAL_MONTHLY'];
		}
		if (str === FeeTypeEnum.VALIDATION) {
			return ['VALIDATION', 'PREPAID_VALIDATION', 'BILL_BACK_VALIDATION'];
		}
		return str.toUpperCase().split(' ').join('_').replace('/', '_');
	}, []);

	const getDownloadParams = useCallback(() => {
		if (isReconciliationReport) {
			return {
				facilityId,
				transactionStartDate: formatDateParkerStatus(transactionStartDate as string),
				...(transactionEndDate ? { transactionEndDate: formatDateParkerStatus(transactionEndDate) } : {}),
				...(prefillAccountId ? { accountId: prefillAccountId } : {}),
			};
		}

		const baseParams = {
			facilityId,
			dateOrder,
		};

		return {
			...baseParams,
			...(accountId ? { accountId } : {}),
			...(subName ? { subscriptionName: subName } : {}),
			...(prefillAccountId ? { accountId: prefillAccountId } : {}),
			...(prefillSubName ? { subscriptionName: prefillSubName } : {}),
			...(enableRateType ? { rateType: rateType?.map(formatRateType).flat() } : {}),
			...(applicationStartDate ? { applicationStartDate: formatDateParkerStatus(applicationStartDate) } : {}),
			...(applicationEndDate ? { applicationEndDate: formatDateParkerStatus(applicationEndDate) } : {}),
			...(transactionStartDate ? { transactionStartDate: formatDateParkerStatus(transactionStartDate) } : {}),
			...(transactionEndDate ? { transactionEndDate: formatDateParkerStatus(transactionEndDate) } : {}),
			...(selectedSublotId && { sublotId: selectedSublotId }),
			...(selectedSublotName && { sublotName: selectedSublotName }),
		};
	}, [
		isReconciliationReport,
		facilityId,
		dateOrder,
		accountId,
		subName,
		prefillAccountId,
		prefillSubName,
		enableRateType,
		rateType,
		formatRateType,
		applicationStartDate,
		applicationEndDate,
		transactionStartDate,
		transactionEndDate,
		selectedSublotId,
		selectedSublotName,
	]);

	const params = useMemo(() => getDownloadParams(), [getDownloadParams]);

	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: `Please select a report type.`,
				failingCondition: !reportType,
			},
			{
				message: `Please select a date order.`,
				failingCondition: !isReconciliationReport && !dateOrder && !isCashBasisPaymentReport,
			},
			{
				message: `Please provide a fee type.`,
				failingCondition: enableRateType && (!rateType || rateType.length === 0),
			},
			{
				message: `Please specify either an Application Start Date or a Transaction Start Date.`,
				failingCondition: !isReconciliationReport && !transactionStartDate && !applicationStartDate,
			},
			{
				message: `Please specify a Transaction Start Date.`,
				failingCondition: isReconciliationReport && !transactionStartDate,
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [
		reportType,
		rateType,
		setShowSnackbar,
		transactionStartDate,
		applicationStartDate,
		isReconciliationReport,
		enableRateType,
		dateOrder,
		isCashBasisPaymentReport,
	]);

	const handleReset = useCallback(() => {
		setReportType('');
		setDateOrder('');
		setTransactionStartDate(null);
		setTransactionEndDate(null);
		setApplicationStartDate(null);
		setApplicationEndDate(null);
		setAccountId('');
		setSubName('');
		setSelectedSublotId(null);
		setSelectedSublotName(null);
		setSelectAll(true);
	}, []);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2}>
				<Grid item xs={4}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel>Report Type</InputLabel>
						<Select
							label='Report Type'
							value={reportType || ''}
							onChange={(e) => setReportType(e.target.value)}
							data-cy='report-type-select-menu'
						>
							{reportTypes?.map((report) => (
								<MenuItem key={report.label} value={report.value}>
									{report.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={4}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel shrink>Rate Type</InputLabel>
						<Select
							label='Rate Type'
							value={rateType}
							multiple
							displayEmpty
							renderValue={(selected) => {
								if (selectAll) return 'All Selected';
								if (selected.length === 0) return 'None Selected';
								if (selected.length === 1) return selected[0];
								return `${selected.length} types are selected`;
							}}
							disabled={!enableRateType}
							data-cy='rate-type-select-menu'
						>
							<MenuItem value=''>
								<Checkbox checked={selectAll} onChange={handleSelectAllCheck} />
								<ListItemText primary={selectAll ? 'All Selected' : 'None Selected'} />
							</MenuItem>
							{rateTypes?.map((fee) => (
								<MenuItem key={fee} value={fee}>
									<Checkbox
										checked={checkedTypes[fee] || false}
										onChange={() => handleSingleCheck(fee)}
									/>
									<ListItemText primary={fee} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={4}>
					<FormControl fullWidth variant='outlined' required>
						<InputLabel>Date Order</InputLabel>
						<Select
							label='Date Order'
							value={dateOrder || ''}
							onChange={(e) => setDateOrder(e.target.value)}
							disabled={isReconciliationReport || isCashBasisPaymentReport}
							data-cy='date-order-select-menu'
						>
							{dateOrderOptions?.map((report) => (
								<MenuItem key={report.label} value={report.value}>
									{report.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs={4}>
					<TextField
						type='number'
						fullWidth
						variant='outlined'
						label='Account ID'
						value={accountId || prefillAccountId || ''}
						onChange={(e) => setAccountId(e.target.value)}
						disabled={forTenant || isReconciliationReport}
						inputProps={{ 'data-cy': 'account-id-input' }}
						onKeyDown={(event) => {
							if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
								event.preventDefault();
							}
						}}
						onPaste={(event) => {
							event.preventDefault();

							const paste = event.clipboardData.getData('text');
							const cleanedPaste = /^[AC]*[-]*(\d+)$/.exec(paste);

							if (cleanedPaste) {
								setAccountId(cleanedPaste[1]);
							}
						}}
						InputProps={{
							startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
						}}
					/>
				</Grid>
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Transaction Start Date'
							value={transactionStartDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'transaction-start-date-picker' },
								},
							}}
							onChange={setTransactionStartDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Application Start Date'
							value={applicationStartDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'application-start-date-picker' },
								},
							}}
							onChange={setApplicationStartDate}
							disabled={isReconciliationReport || isCashBasisPaymentReport}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={4}>
					<TextField
						fullWidth
						variant='outlined'
						label='Sub Name'
						value={subName || prefillSubName || ''}
						onChange={(e) => setSubName(e.target.value)}
						disabled={forTenant || isReconciliationReport}
						inputProps={{ 'data-cy': 'sub-name-input' }}
					/>
				</Grid>
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Transaction End Date'
							value={transactionEndDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'transaction-end-date-picker' },
								},
							}}
							onChange={setTransactionEndDate}
						/>
					</LocalizationProvider>
				</Grid>
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='Application End Date'
							value={applicationEndDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'application-end-date-picker' },
								},
							}}
							onChange={setApplicationEndDate}
							disabled={isReconciliationReport || isCashBasisPaymentReport}
						/>
					</LocalizationProvider>
				</Grid>
				{reportType !== 'ReconciliationReport' && (
					<Grid item xs={4}>
						<FormControl fullWidth variant='outlined'>
							<InputLabel>Sublot</InputLabel>
							<Select
								label='Sublot'
								value={selectedSublotId || ''}
								onChange={(e) => {
									const subId = Number(e.target.value);
									setSelectedSublotId(subId);
									const selectedSublot = sublotsData?.getSublots.find((d) => d.id === subId);
									setSelectedSublotName(selectedSublot.name);
								}}
							>
								{sublotsData?.getSublots?.map((sublot, index) => (
									<MenuItem key={index} value={sublot.id}>
										{sublot.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				)}
			</Grid>
			<ReportActionButton
				downloadReport={(format) =>
					downloadReport(format, {
						reportType: reportType as string,
						params,
						handleValidation,
						setIsDownloadingCsv,
						setIsDownloadingPdf,
					})
				}
				disableDownloadButtons={!facilityId}
				isDownloadingPdf={isDownloadingPdf}
				isDownloadingCsv={isDownloadingCsv}
				onReset={handleReset}
			/>
		</Box>
	);
};
